import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import type { AutomationStepNodeData, BaseNodeData, ParentCaseInfo, SegmentNodeData } from '../types';
import { NODE_TYPES } from '../types';
import { getPathNodeIdsToRoot } from './flowPathing';
import { extractPrimaryAndAdditionalCriteria, primaryCriteriaHasValue } from './segmentCriteria';

export interface ValidationError {
	type: 'trigger' | 'step' | 'general';
	message: string;
	stepId?: string;
	nodeId?: string;
	pathNodeIds?: string[];
	severity: 'error' | 'warning';
}

export interface ValidationResult {
	isValid: boolean;
	errors: ValidationError[];
	warnings: ValidationError[];
}

export interface ValidateAutomationOptions {
	/**
	 * Optional expanded flow graph.
	 *
	 * When provided, enables validations that need to inspect segments/branches
	 * (e.g., ensuring segments contain steps) and validate expanded child-template
	 * steps rendered in the flow graph.
	 */
	flow?: {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
	};
}

export function validateAutomationTemplate(
	template: Api.IAutomationTemplate,
	options?: ValidateAutomationOptions
): ValidationResult {
	const errors: ValidationError[] = [];
	const warnings: ValidationError[] = [];

	if (!template) {
		errors.push({
			type: 'general',
			message: 'Template is missing',
			severity: 'error',
		});
		return { isValid: false, errors, warnings };
	}

	const version = template.draft || template.published;
	if (!version) {
		errors.push({
			type: 'general',
			message: 'No version found for this template',
			severity: 'error',
		});
		return { isValid: false, errors, warnings };
	}

	if (!version.trigger) {
		errors.push({
			type: 'trigger',
			message: 'Automation must have a trigger configured',
			severity: 'error',
		});
	}

	const steps = version.steps || [];
	if (steps.length === 0) {
		warnings.push({
			type: 'general',
			message: 'Automation has no steps. Consider adding at least one step.',
			severity: 'warning',
		});
	}

	const validatedStepIds = new Set<string>();
	steps.forEach((step, index) => {
		if (typeof step.id === 'string' && step.id.length > 0) {
			validatedStepIds.add(step.id);
		}
		const stepValidation = validateStep(step, index, options, version.trigger);
		errors.push(...stepValidation.errors);
		warnings.push(...stepValidation.warnings);
	});

	if (options?.flow) {
		const flowValidation = validateExpandedFlowSteps(options.flow, validatedStepIds, version.trigger);
		errors.push(...flowValidation.errors);
		warnings.push(...flowValidation.warnings);
	}

	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	};
}

function isRichTextContentEmpty(content?: unknown): boolean {
	if (!content) {
		return true;
	}

	// Some legacy/new-step paths may store content as an HTML string.
	if (typeof content === 'string') {
		const trimmed = content.trim();
		return trimmed === '' || trimmed === '<p></p>' || trimmed === '<p><br></p>';
	}

	// Current API shape: { document: '<p>...</p>' }
	if (typeof content === 'object') {
		const doc = (content as { document?: unknown }).document;
		if (typeof doc !== 'string') {
			return true;
		}
		const trimmedDocument = doc.trim();
		return trimmedDocument === '' || trimmedDocument === '<p></p>' || trimmedDocument === '<p><br></p>';
	}

	return true;
}

function hasNonEmptyString(value: unknown): boolean {
	return typeof value === 'string' && value.trim().length > 0;
}

function filterHasAnyValue(filter?: Api.IContactFilterCriteria): boolean {
	if (!filter) {
		return false;
	}

	if (hasNonEmptyString(filter.value)) {
		return true;
	}

	if (Array.isArray(filter.criteria)) {
		return filter.criteria.some(child => filterHasAnyValue(child));
	}

	return false;
}

function getSegmentDisplayName(
	segmentIndex: number,
	segmentName?: string,
	isDefault?: boolean,
	globalSegmentOrdinal?: number
): string {
	if (isDefault) {
		return 'Default segment';
	}
	if (segmentName && segmentName.trim() !== '') {
		return `Segment "${segmentName.trim()}"`;
	}
	const ordinal = typeof globalSegmentOrdinal === 'number' ? globalSegmentOrdinal : segmentIndex + 1;
	return `Segment #${ordinal}`;
}

function isGeneratedCaseName(caseName?: string): boolean {
	if (!caseName) {
		return false;
	}

	return /^case\s+\d+$/i.test(caseName.trim());
}

function getExplicitSegmentLabelFromParentCaseInfo(parentCaseInfo?: ParentCaseInfo): string | null {
	if (!parentCaseInfo || parentCaseInfo.isBehaviorCase) {
		return null;
	}

	if (parentCaseInfo.isDefault) {
		return 'Default segment';
	}

	const caseName = typeof parentCaseInfo.caseName === 'string' ? parentCaseInfo.caseName.trim() : '';
	if (caseName && !isGeneratedCaseName(caseName)) {
		return `Segment "${caseName}"`;
	}

	if (typeof parentCaseInfo.globalSegmentOrdinal === 'number') {
		return `Segment #${parentCaseInfo.globalSegmentOrdinal}`;
	}

	return null;
}

function getIncomingBranchSegmentLabel(targetNodeId: string, edges: Edge[]): string | null {
	const incomingEdge = edges.find(edge => edge.target === targetNodeId && edge.type !== 'phantomAddPath');
	const edgeData = incomingEdge?.data as { caseName?: unknown; isDefault?: unknown } | undefined;
	if (!edgeData) {
		return null;
	}

	if (edgeData.isDefault === true) {
		return 'Default segment';
	}

	const caseName = typeof edgeData.caseName === 'string' ? edgeData.caseName.trim() : '';
	if (caseName && !isGeneratedCaseName(caseName)) {
		return `Segment "${caseName}"`;
	}

	return null;
}

function isGenericSegmentLabel(label: string | null | undefined): boolean {
	if (!label) {
		return true;
	}

	return label === 'Segment' || /^Segment #\d+$/i.test(label);
}

function getSegmentNodeContextLabel(
	segmentNode: Node<BaseNodeData>,
	flow?: {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
	}
): string {
	const segmentData = segmentNode.data as Partial<SegmentNodeData>;
	const fallbackLabel = getSegmentDisplayName(
		typeof segmentData.segmentIndex === 'number' ? segmentData.segmentIndex : 0,
		segmentData.segmentName,
		segmentData.isDefault,
		segmentData.globalSegmentOrdinal
	);

	if (segmentData.isDefault || hasNonEmptyString(segmentData.segmentName)) {
		return fallbackLabel;
	}

	const incomingBranchLabel = flow ? getIncomingBranchSegmentLabel(segmentNode.id, flow.edges) : null;
	return incomingBranchLabel || fallbackLabel;
}

function getFlowStepLabel(step: Api.IAutomationStep, nodeData?: Partial<AutomationStepNodeData>): string {
	const stepName = (step as { name?: unknown }).name;
	if (hasNonEmptyString(stepName)) {
		return `Step "${(stepName as string).trim()}"`;
	}

	const nodeLabel = nodeData?.label;
	if (hasNonEmptyString(nodeLabel)) {
		return `Step "${(nodeLabel as string).trim()}"`;
	}

	return 'Step';
}

function getFlowBranchContextLabel(
	node: Node<BaseNodeData>,
	flow: {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
	}
): string | null {
	const nodeById = new Map(flow.nodes.map(candidate => [candidate.id, candidate] as const));
	const pathNodeIds = getPathNodeIdsToRoot(node.id, flow.edges);
	const nodeData = node.data as Partial<AutomationStepNodeData> | undefined;
	const parentCaseInfo = nodeData?.parentCaseInfo;
	const explicitParentSegmentLabel = getExplicitSegmentLabelFromParentCaseInfo(parentCaseInfo);

	for (let index = pathNodeIds.length - 1; index >= 0; index--) {
		const pathNode = nodeById.get(pathNodeIds[index]);
		if (!pathNode || pathNode.type !== NODE_TYPES.SEGMENT) {
			continue;
		}

		const segmentLabel = getSegmentNodeContextLabel(pathNode, flow);
		if (isGenericSegmentLabel(segmentLabel) && explicitParentSegmentLabel) {
			return explicitParentSegmentLabel;
		}
		return segmentLabel;
	}

	for (let index = pathNodeIds.length - 1; index >= 0; index--) {
		const pathNode = nodeById.get(pathNodeIds[index]);
		if (!pathNode || pathNode.type !== NODE_TYPES.BEHAVIOR_CASE) {
			continue;
		}

		const behaviorCaseData = pathNode.data as Partial<BaseNodeData> | undefined;
		if (hasNonEmptyString(behaviorCaseData?.label)) {
			return (behaviorCaseData.label as string).trim();
		}
	}

	if (!parentCaseInfo || typeof parentCaseInfo !== 'object') {
		return null;
	}

	if (parentCaseInfo.isBehaviorCase && hasNonEmptyString(parentCaseInfo.caseName)) {
		return (parentCaseInfo.caseName as string).trim();
	}

	if (explicitParentSegmentLabel) {
		return explicitParentSegmentLabel;
	}

	if (typeof parentCaseInfo.caseIndex === 'number') {
		return `Segment #${parentCaseInfo.caseIndex + 1}`;
	}

	return null;
}

function validateExpandedFlowSteps(
	flow: {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
	},
	validatedStepIds: Set<string>,
	trigger?: Api.IAutomationTrigger
): ValidationResult {
	const errors: ValidationError[] = [];
	const warnings: ValidationError[] = [];

	for (const node of flow.nodes) {
		const nodeData = node.data as Partial<AutomationStepNodeData> | undefined;
		const step = nodeData?.automationStep;
		if (!step || typeof step !== 'object' || !('_type' in step) || !('id' in step)) {
			continue;
		}

		const stepId = (step as { id?: unknown }).id;
		if (typeof stepId !== 'string' || stepId.length === 0) {
			continue;
		}

		if (validatedStepIds.has(stepId)) {
			continue;
		}

		validatedStepIds.add(stepId);
		const baseStepLabel = getFlowStepLabel(step as Api.IAutomationStep, nodeData);
		const branchContextLabel = getFlowBranchContextLabel(node, flow);
		const stepLabel = branchContextLabel ? `${branchContextLabel} → ${baseStepLabel}` : baseStepLabel;
		const pathNodeIds = getPathNodeIdsToRoot(node.id, flow.edges);
		const stepValidation = validateStep(step as Api.IAutomationStep, 0, { flow }, trigger, stepLabel);

		errors.push(
			...stepValidation.errors.map(error => ({
				...error,
				nodeId: error.nodeId || node.id,
				pathNodeIds: error.pathNodeIds || pathNodeIds,
			}))
		);
		warnings.push(
			...stepValidation.warnings.map(warning => ({
				...warning,
				nodeId: warning.nodeId || node.id,
				pathNodeIds: warning.pathNodeIds || pathNodeIds,
			}))
		);
	}

	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	};
}

// Validate email step fields
function validateEmailStep(step: Api.IEmailAutomationStep, stepLabel: string): ValidationError[] {
	const errors: ValidationError[] = [];

	if (!step.name || step.name.trim() === '') {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Email name is required`,
			stepId: step.id,
			severity: 'error',
		});
	}
	const subject = step.options?.subject ?? step.subject;
	if (!hasNonEmptyString(subject)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Email subject is required`,
			stepId: step.id,
			severity: 'error',
		});
	}
	if (isRichTextContentEmpty(step.content)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Email content is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate texting step fields
function validateTextingStep(step: Api.ITextingAutomationStep, stepLabel: string): ValidationError[] {
	const errors: ValidationError[] = [];

	if (isRichTextContentEmpty(step.content)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Text message content is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate action item step fields
function validateActionItemStep(step: Api.IActionItemAutomationStep, stepLabel: string): ValidationError[] {
	const errors: ValidationError[] = [];

	if (isRichTextContentEmpty(step.content)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Action item content is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate tag step fields
function validateTagStep(
	step: Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep,
	stepLabel: string
): ValidationError[] {
	const errors: ValidationError[] = [];

	if (!step.tagName || step.tagName.trim() === '') {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Tag name is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate handwritten card step fields
function validateHandwrittenCardStep(
	step: Api.HandwrittenCardAutomationStep,
	stepLabel: string,
	trigger?: Api.IAutomationTrigger
): ValidationError[] {
	const errors: ValidationError[] = [];

	if (!step.templateReference) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Handwritten card template is required`,
			stepId: step.id,
			severity: 'error',
		});
	}
	if (isRichTextContentEmpty(step.content)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Handwritten card content is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	const triggerType = (trigger as any)?._type as string | undefined;
	const isEventBasedTrigger =
		triggerType === 'MeetingAutomationTrigger' || triggerType === 'EventRegistrationAutomationTrigger';
	if (!isEventBasedTrigger && step.schedule && step.schedule.numberOfHours == null) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: You must choose a time to send the message`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate newsletter step fields
function validateNewsletterStep(step: Api.IHtmlNewsletterAutomationStep, stepLabel: string): ValidationError[] {
	const errors: ValidationError[] = [];

	if (!step.name || step.name.trim() === '') {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Newsletter name is required`,
			stepId: step.id,
			severity: 'error',
		});
	}
	const subject = step.options?.subject ?? step.subject;
	if (!hasNonEmptyString(subject)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Newsletter subject is required`,
			stepId: step.id,
			severity: 'error',
		});
	}
	if (isRichTextContentEmpty(step.content)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Newsletter content is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

// Validate opportunity step fields
function validateOpportunityStep(step: Api.IOpportunityAutomationStep, stepLabel: string): ValidationError[] {
	const warnings: ValidationError[] = [];

	if (step.dealSize !== undefined && step.dealSize < 0) {
		warnings.push({
			type: 'step',
			message: `${stepLabel}: Opportunity amount should be positive`,
			stepId: step.id,
			severity: 'warning',
		});
	}

	return warnings;
}

type TriggerAutomationStepLike = Omit<Api.IAutomationStep, '_type'> & {
	_type?: Api.ApiModelType | 'TriggerAutomationStep';
	targetAutomationTemplateId?: string;
};

function validateTriggerAutomationStep(step: TriggerAutomationStepLike, stepLabel: string): ValidationError[] {
	const errors: ValidationError[] = [];

	if (!hasNonEmptyString(step.targetAutomationTemplateId)) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Select an automation to start`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return errors;
}

function validateSwitchStep(
	switchStep: Api.ISwitchAutomationStep,
	stepLabel: string,
	options?: ValidateAutomationOptions
): ValidationError[] {
	const errors: ValidationError[] = [];
	const cases = switchStep.cases || [];
	const flow = options?.flow;
	const switchNodeId = `step-${switchStep.id}`;
	const segmentNodes = flow
		? (flow.nodes.filter(
				n => n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === switchNodeId
			) as Node<SegmentNodeData>[])
		: [];

	const findSegmentNodeForCase = (caseStatement: Api.IAutomationStepCaseStatement, index: number) => {
		return segmentNodes.find(segmentNode => {
			const segmentData = segmentNode.data as SegmentNodeData;
			if (caseStatement.id && segmentData.caseId === caseStatement.id) {
				return true;
			}
			return segmentData.segmentIndex === index;
		});
	};

	const getSegmentContextLabel = (caseStatement: Api.IAutomationStepCaseStatement, index: number): string => {
		const matchingSegmentNode = findSegmentNodeForCase(caseStatement, index);
		if (matchingSegmentNode) {
			return getSegmentNodeContextLabel(matchingSegmentNode as Node<BaseNodeData>, flow);
		}
		return getSegmentDisplayName(index, caseStatement.name, caseStatement.isDefault);
	};

	const getSegmentPathNodeIds = (
		caseStatement: Api.IAutomationStepCaseStatement,
		index: number
	): string[] | undefined => {
		const matchingSegmentNode = findSegmentNodeForCase(caseStatement, index);
		if (!matchingSegmentNode || !flow) {
			return undefined;
		}
		return getPathNodeIdsToRoot(matchingSegmentNode.id, flow.edges);
	};

	const getSegmentNodeId = (caseStatement: Api.IAutomationStepCaseStatement, index: number): string | undefined => {
		return findSegmentNodeForCase(caseStatement, index)?.id;
	};

	if (cases.length === 0) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Conditional step must have at least one segment`,
			stepId: switchStep.id,
			severity: 'error',
		});
		return errors;
	}

	// Validate case filters (segments must have a primary filter configured).
	cases.forEach((caseStatement, index) => {
		if (caseStatement.isDefault) {
			return;
		}

		const segmentContextLabel = getSegmentContextLabel(caseStatement, index);
		const nodeId = getSegmentNodeId(caseStatement, index);
		const pathNodeIds = getSegmentPathNodeIds(caseStatement, index);
		const { primaryCriteria } = extractPrimaryAndAdditionalCriteria(caseStatement.filter, false);
		const hasPrimary = primaryCriteriaHasValue(primaryCriteria);
		if (!hasPrimary) {
			errors.push({
				type: 'step',
				message: `${segmentContextLabel} must have a filter configured (choose a primary filter)`,
				stepId: switchStep.id,
				nodeId,
				pathNodeIds,
				severity: 'error',
			});
			return;
		}

		if (!filterHasAnyValue(caseStatement.filter)) {
			errors.push({
				type: 'step',
				message: `${segmentContextLabel} must have a filter configured`,
				stepId: switchStep.id,
				nodeId,
				pathNodeIds,
				severity: 'error',
			});
		}
	});

	// Validate that segments contain steps (requires expanded flow graph).
	segmentNodes.forEach(segmentNode => {
		const hasDownstream = flow?.edges.some(edge => edge.source === segmentNode.id);
		if (!hasDownstream) {
			errors.push({
				type: 'step',
				message: `${getSegmentNodeContextLabel(segmentNode as Node<BaseNodeData>, flow)} must contain at least one step`,
				stepId: switchStep.id,
				nodeId: segmentNode.id,
				pathNodeIds: getPathNodeIdsToRoot(segmentNode.id, flow!.edges),
				severity: 'error',
			});
		}
	});

	return errors;
}

function validateStep(
	step: Api.IAutomationStep,
	index: number,
	options?: ValidateAutomationOptions,
	trigger?: Api.IAutomationTrigger,
	stepLabelOverride?: string
): ValidationResult {
	const errors: ValidationError[] = [];
	const warnings: ValidationError[] = [];
	const stepLabel = stepLabelOverride ?? `Step ${index + 1}`;
	const modelType = (step._type || '') as Api.ApiModelType | 'TriggerAutomationStep';

	switch (modelType) {
		case 'EmailAutomationStep':
			errors.push(...validateEmailStep(step as Api.IEmailAutomationStep, stepLabel));
			break;
		case 'TextingAutomationStep':
			errors.push(...validateTextingStep(step as Api.ITextingAutomationStep, stepLabel));
			break;
		case 'ActionItemAutomationStep':
			errors.push(...validateActionItemStep(step as Api.IActionItemAutomationStep, stepLabel));
			break;
		case 'AddTagAutomationStep':
		case 'RemoveTagAutomationStep':
			errors.push(...validateTagStep(step as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep, stepLabel));
			break;
		case 'HandwrittenCardAutomationStep':
			errors.push(...validateHandwrittenCardStep(step as Api.HandwrittenCardAutomationStep, stepLabel, trigger));
			break;
		case 'HtmlNewsletterAutomationStep':
			errors.push(...validateNewsletterStep(step as Api.IHtmlNewsletterAutomationStep, stepLabel));
			break;
		case 'OpportunityAutomationStep':
			warnings.push(...validateOpportunityStep(step as Api.IOpportunityAutomationStep, stepLabel));
			break;
		case 'SwitchAutomationStep':
			errors.push(...validateSwitchStep(step as Api.ISwitchAutomationStep, stepLabel, options));
			break;
		case 'TriggerAutomationStep':
			errors.push(...validateTriggerAutomationStep(step as TriggerAutomationStepLike, stepLabel));
			break;
		case 'NoActionAutomationStep':
			break;
		default:
			warnings.push({
				type: 'step',
				message: `${stepLabel}: Unknown step type: ${modelType}`,
				stepId: step.id,
				severity: 'warning',
			});
	}

	// Most steps require a schedule (timing) configuration.
	// Action items are a special case: schedule represents the *due date* and can be omitted for "No due date".
	const scheduleIsRequired = modelType !== 'ActionItemAutomationStep';
	if (scheduleIsRequired && !step.schedule) {
		errors.push({
			type: 'step',
			message: `${stepLabel}: Schedule configuration is required`,
			stepId: step.id,
			severity: 'error',
		});
	}

	return { isValid: errors.length === 0, errors, warnings };
}
